/**
 * @solid-analytics/solidstart
 *
 * SolidStart integration with server-side tracking support
 */

import {
  createContext,
  useContext,
  onMount,
  type Component,
  type JSX,
} from "solid-js";
import { isServer } from "solid-js/web";
import type { AnalyticsInstance } from "analytics";
import { createAnalyticsRuntime } from "@solid-analytics/solid/runtime";
import type {
  AnalyticsRuntime,
  AnalyticsOptions as BaseAnalyticsOptions,
} from "@solid-analytics/solid/runtime";
import {
  serverTrack,
  serverIdentify,
  serverPage,
  type ServerTrackOptions,
} from "./server";

export interface SolidStartAnalyticsOptions extends BaseAnalyticsOptions {
  /**
   * Server-side tracking mode
   * - 'server-function': Use server functions (default for SolidStart)
   * - 'queue': Queue events during SSR, replay on client
   * - 'track': Track immediately on server
   * - 'both': Track on both server and client
   * - false: Client-only
   *
   * @default 'server-function'
   */
  ssr?: "server-function" | "queue" | "track" | "both" | false;

  /**
   * Server tracking options (only applies when ssr is 'server-function')
   */
  serverOptions?: Omit<ServerTrackOptions, "client">;
}

export interface AnalyticsProviderProps {
  /**
   * Analytics client instance (must support SSR if using server-side tracking)
   */
  client?: AnalyticsInstance;

  /**
   * Configuration options
   */
  options?: SolidStartAnalyticsOptions;

  /**
   * Children
   */
  children: JSX.Element;
}

// Context for analytics runtime
const AnalyticsContext = createContext<AnalyticsRuntime>();

/**
 * SolidStart Analytics Provider
 *
 * Provides analytics context with automatic server-side tracking support
 *
 * @example
 * ```tsx
 * import { AnalyticsProvider } from '@solid-analytics/solidstart'
 * import Analytics from 'analytics'
 * import segmentPlugin from '@analytics/segment'
 *
 * const client = Analytics({
 *   app: 'my-app',
 *   plugins: [segmentPlugin({ writeKey: 'key' })]
 * })
 *
 * export default function App() {
 *   return (
 *     <AnalyticsProvider
 *       client={client}
 *       options={{ ssr: 'server-function' }}
 *     >
 *       <button analytics:click="purchase">Buy</button>
 *     </AnalyticsProvider>
 *   )
 * }
 * ```
 */
export const AnalyticsProvider: Component<AnalyticsProviderProps> = (props) => {
  // Default to server-function mode for SolidStart
  const ssrMode = props.options?.ssr ?? "server-function";

  // Create runtime with SolidStart-specific defaults
  const runtime = createAnalyticsRuntime({
    ...props.options,
    client: props.client,
    ssr: ssrMode === "server-function" ? false : ssrMode,
  });

  // If using server-function mode, wrap track/identify/page with server functions
  if (ssrMode === "server-function" && !isServer) {
    const serverOptions = {
      ...props.options?.serverOptions,
      client: props.client,
    };

    // Wrap runtime methods to use server functions
    const originalTrack = runtime.track;
    const originalIdentify = runtime.identify;
    const originalPage = runtime.page;

    runtime.track = async (
      eventName: string,
      properties?: Record<string, any>,
      domEvent?: any
    ) => {
      // Call server function for server-side tracking
      await serverTrack(eventName, properties, serverOptions);

      // Also track on client if configured
      if (props.options?.serverOptions?.debug || props.options?.serverOptions?.debug) {
        await originalTrack(eventName, properties, domEvent);
      }
    };

    runtime.identify = async (userId: string, traits?: Record<string, any>) => {
      await serverIdentify(userId, traits, serverOptions);

      if (props.options?.serverOptions?.debug || props.options?.serverOptions?.debug) {
        await originalIdentify(userId, traits);
      }
    };

    runtime.page = async (
      pageName?: string,
      properties?: Record<string, any>
    ) => {
      await serverPage(pageName, properties, serverOptions);

      if (props.options?.serverOptions?.debug || props.options?.serverOptions?.debug) {
        await originalPage(pageName, properties);
      }
    };
  }

  // Set global runtime for Babel plugin
  if (typeof globalThis !== "undefined") {
    (globalThis as any).__analytics = runtime;
  }

  // Handle visibility tracking on mount (client-only)
  onMount(() => {
    if (isServer) return;

    // Set up Intersection Observer for visibility tracking
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            const eventName = element.dataset.analyticsVisible;
            const payloadStr = element.dataset.analyticsVisiblePayload;

            if (eventName) {
              let payload: any;
              try {
                payload = payloadStr ? JSON.parse(payloadStr) : undefined;
              } catch (e) {
                console.warn("[Analytics] Failed to parse visibility payload");
              }

              runtime.track(eventName, payload);

              // Unobserve after tracking (track once)
              observer.unobserve(element);
            }
          }
        });
      },
      {
        threshold: 0.5, // 50% visible
        rootMargin: "0px",
      }
    );

    // Observe all elements with data-analytics-visible
    document.querySelectorAll("[data-analytics-visible]").forEach((el) => {
      observer.observe(el);
    });

    // Handle identify on mount
    const identifyElements = document.querySelectorAll(
      "[data-analytics-identify]"
    );
    identifyElements.forEach((el) => {
      try {
        const data = JSON.parse(
          el.getAttribute("data-analytics-identify") || "[]"
        );
        const [userId, traits] = data;
        if (userId) {
          runtime.identify(userId, traits);
        }
      } catch (e) {
        console.warn("[Analytics] Failed to parse identify data");
      }
    });

    // Handle page tracking on mount
    const pageElements = document.querySelectorAll("[data-analytics-page]");
    pageElements.forEach((el) => {
      try {
        const data = JSON.parse(el.getAttribute("data-analytics-page") || "[]");
        const [pageName, properties] = data;
        runtime.page(pageName, properties);
      } catch (e) {
        console.warn("[Analytics] Failed to parse page data");
      }
    });
  });

  return (
    <AnalyticsContext.Provider value={runtime}>
      {props.children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Hook to access analytics context
 *
 * @example
 * ```tsx
 * import { useAnalytics } from '@solid-analytics/solidstart'
 *
 * function MyComponent() {
 *   const analytics = useAnalytics()
 *
 *   const handleClick = () => {
 *     analytics.track('button_clicked', { button: 'cta' })
 *   }
 *
 *   return <button onClick={handleClick}>Click</button>
 * }
 * ```
 */
export function useAnalytics(): AnalyticsRuntime {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }

  return context;
}

// Re-export server functions for convenience
export {
  serverTrack,
  serverIdentify,
  serverPage,
  serverTrackBatch,
  createServerAnalytics,
} from "./server";
export type { ServerTrackOptions, RequestContext } from "./server";

// Re-export AnalyticsRuntime type (already imported above)
export type { AnalyticsRuntime };
